import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import CourseRegisterModal from "../../components/CourseRegisterModal";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";

interface Course {
  id: number;
  slug: string;
  title: string;
  description: string;
  image_url: string;
  detail_image_url?: string;
  day_time?: string | null;
  age_group?: string | null;
}

const CoursePage = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetch(`/api/courses?lang=${i18n.language}`, {
      headers: { Accept: "application/json" },
    })
      .then((res) => {
        if (!res.ok) throw new Error("courses fetch failed");
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setCourses(data);
        } else {
          console.error("Unexpected response format:", data);
          setCourses([]);
        }
      })
      .catch((err) => console.error("Courses fetch error:", err));
  }, [i18n.language]);

  const handleRegisterClick = (courseId: number) => {
    setSelectedCourseId(courseId);
    setModalOpen(true);
  };

  const handleClose = () => setModalOpen(false);

  const handleSuccessClose = () => {
    setModalOpen(false);
    navigate("/");
  };

  return (
          <section className="w-full min-h-screen bg-black text-white font-sans px-6 sm:px-8 md:px-10 py-1 mt-8">
      <SEO
        title={t("pageTitles.courses")}
        description="Berlin'de tiyatro ve piyano kursları. Yetişkinler, gençler ve çocuklar için profesyonel eğitmenlerle sanat kursları. Hemen kayıt olun!"
        keywords="tiyatro kursu, piyano kursu, berlin kursları, çocuk kursları, sanat eğitimi, theater kurs, piano kurs"
      />
      <div className="w-full max-w-5xl mx-auto">
        <h1 className="text-3xl font-extrabold text-center underline underline-offset-8 decoration-brand-red-text mb-6">
          {t("courses.title")}
        </h1>

        <p className="text-center text-gray-400 max-w-3xl mx-auto mb-16 text-lg">
          {t("courses.intro")}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mx-auto px-6">
          {courses.map((course) => (
            <div
              key={course.id}
              className="bg-zinc-900 rounded-xl overflow-hidden shadow-lg flex flex-col justify-between"
            >
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Course",
                    name: course.title,
                    description: course.description ?? "",
                    provider: {
                      "@type": "Organization",
                      name: "Internationale in Kunst e.V.",
                    },
                  }).replace(/</g, "\\u003c"),
                }}
              />
              <div className="w-full aspect-[3/4] overflow-hidden">
                <img
                  src={course.image_url}
                  alt={course.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="p-6 md:p-7 flex flex-col flex-grow">
                <h3 className="text-2xl font-bold mb-3">{course.title}</h3>
                {(course.day_time || course.age_group) && (
                  <p className="text-sm text-gray-400 mb-3">
                    {course.day_time && <span>{t("courses.dayTime")}: {course.day_time}</span>}
                    {course.day_time && course.age_group && <span className="mx-2">·</span>}
                    {course.age_group && <span>{t("courses.ageGroup")}: {course.age_group}</span>}
                  </p>
                )}
                <p className="text-base text-gray-300 flex-grow">
                  {(course.description ?? "").substring(0, 150)}
                  {(course.description?.length ?? 0) > 150 ? "..." : ""}
                </p>

                <div className="mt-6 flex justify-between items-center">
                  <Link
                    to={`/courses/${course.slug}`}
                    className="text-brand-red-text font-semibold hover:underline"
                  >
                    {t("courses.readMore")}
                  </Link>
                  <button
                    className="bg-[#D8001D] hover:bg-[#B8001A] transition-colors duration-200 text-white px-4 py-2 rounded-md text-sm font-semibold cursor-pointer"
                    onClick={() => handleRegisterClick(course.id)}
                  >
                    {t("courses.register")}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {selectedCourseId && (
        <CourseRegisterModal
          courseId={selectedCourseId}
          isOpen={isModalOpen}
          onClose={handleClose}
          onSuccessClose={handleSuccessClose}
        />
      )}
    </section>
  );
};

export default CoursePage;